import { useCallback, createContext, useContext, useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';

const TransitionContext = createContext();

export const useTransition = () => useContext(TransitionContext);

export const TransitionProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const path1Ref = useRef(null);
  const path2Ref = useRef(null);
  const overlayRef = useRef(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [nextPath, setNextPath] = useState(null);
  const [progress, setProgress] = useState(0);
  const isAnimating = useRef(false);
  const lastPathname = useRef(location.pathname);

  // Codegrid path data
  const PATH_1_D = "M227.549 1818.76C227.549 1818.76 406.016 2207.75 569.049 2130.26C843.431 1999.85 -264.104 1002.3 227.549 876.262C552.918 792.849 773.647 2456.11 1342.05 2130.26C1885.43 1818.76 14.9644 455.772 760.548 137.262C1342.05 -111.152 1663.5 2266.35 2209.55 1972.76C2755.6 1679.18 1536.63 384.467 1826.55 137.262C2013.5 -22.1463 2209.55 381.262 2209.55 381.262";
  const PATH_2_D = "M1661.28 2255.51C1661.28 2255.51 2311.09 1960.37 2111.78 1817.01C1944.47 1696.67 718.456 2870.17 499.781 2255.51C308.969 1719.17 2457.51 1613.83 2111.78 963.512C1766.05 313.198 427.949 2195.17 132.281 1455.51C-155.219 736.292 2014.78 891.514 1708.78 252.012C1437.81 -314.29 369.471 909.169 132.281 566.512C18.1772 401.672 244.781 193.012 244.781 193.012";

  // Initialize SVG paths
  useEffect(() => {
    const p1 = path1Ref.current;
    const p2 = path2Ref.current;
    if (!p1 || !p2) return;

    const l1 = p1.getTotalLength();
    const l2 = p2.getTotalLength();

    gsap.set(p1, { strokeDasharray: l1, strokeDashoffset: l1, attr: { 'stroke-width': 200 } });
    gsap.set(p2, { strokeDasharray: l2, strokeDashoffset: l2, attr: { 'stroke-width': 200 } });
  }, []);

  // Animate transition out - draw paths to cover screen (codegrid "leave")
  const transitionOut = useCallback(() => {
    return new Promise((resolve) => {
      if (isAnimating.current) {
        resolve();
        return;
      }

      isAnimating.current = true;
      setIsTransitioning(true);

      const p1 = path1Ref.current;
      const p2 = path2Ref.current;
      if (!p1 || !p2) {
        isAnimating.current = false;
        setIsTransitioning(false);
        resolve();
        return;
      }

      let l1 = p1.getTotalLength();
      let l2 = p2.getTotalLength();

      // Reset to hidden state
      gsap.set(overlayRef.current, { display: 'block' });
      gsap.set(p1, { strokeDasharray: l1, strokeDashoffset: l1, attr: { 'stroke-width': 200 } });
      gsap.set(p2, { strokeDasharray: l2, strokeDashoffset: l2, attr: { 'stroke-width': 200 } });

      const tl = gsap.timeline({ onComplete: resolve });

      // codegrid: strokes draw in + thicken to cover viewport
      tl.to([p1, p2], {
        strokeDashoffset: 0,
        attr: { 'stroke-width': 700 },
        duration: 1,
        ease: 'power2.inOut',
        stagger: 0,
      }, 0);
    });
  }, []);

  // Animate transition in - undraw paths to reveal screen (codegrid "enter")
  const transitionIn = useCallback(() => {
    return new Promise((resolve) => {
      const p1 = path1Ref.current;
      const p2 = path2Ref.current;
      if (!p1 || !p2) {
        isAnimating.current = false;
        setIsTransitioning(false);
        setNextPath(null);
        resolve();
        return;
      }

      const l1 = p1.getTotalLength();
      const l2 = p2.getTotalLength();

      const tl = gsap.timeline({
        onComplete: () => {
          isAnimating.current = false;
          setIsTransitioning(false);
          setNextPath(null);
          gsap.set(overlayRef.current, { display: 'none' });
          resolve();
        }
      });

      // codegrid: strokes continue sliding off + thin out
      tl.to(p1, {
        strokeDashoffset: -l1,
        attr: { 'stroke-width': 200 },
        duration: 1,
        ease: 'power2.inOut',
      }, 0);
      tl.to(p2, {
        strokeDashoffset: -l2,
        attr: { 'stroke-width': 200 },
        duration: 1,
        ease: 'power2.inOut',
      }, 0);
    });
  }, []);

  const progressTimers = useRef([]);

  const startTransition = useCallback(async (to) => {
    if (to === location.pathname || isAnimating.current) return;

    progressTimers.current.forEach(clearTimeout);
    progressTimers.current = [];

    setNextPath(to);

    // Progress bar animation
    setProgress(15);
    const t1 = setTimeout(() => setProgress(70), 180);
    const t2 = setTimeout(() => setProgress(100), 420);
    const t3 = setTimeout(() => setProgress(0), 760);
    progressTimers.current = [t1, t2, t3];

    await transitionOut();
    navigate(to);
    window.scrollTo(0, 0);
  }, [location.pathname, navigate, transitionOut]);

  useEffect(() => {
    return () => {
      progressTimers.current.forEach(clearTimeout);
      progressTimers.current = [];
    };
  }, []);

  // Handle transition in when location changes
  useEffect(() => {
    if (isAnimating.current && nextPath && location.pathname === nextPath) {
      transitionIn();
    }
    lastPathname.current = location.pathname;
  }, [location.pathname, nextPath, transitionIn]);

  const value = {
    startTransition,
    isTransitioning,
    progress,
    path1Ref,
    path2Ref,
    overlayRef,
    PATH_1_D,
    PATH_2_D,
  };

  return (
    <TransitionContext.Provider value={value}>
      {children}
    </TransitionContext.Provider>
  );
};
